import { GoogleGenerativeAI } from '@google/generative-ai';

const genAI = new GoogleGenerativeAI(process.env.REACT_APP_GEMINI_API_KEY || '');

// Model is env-overridable so it can be bumped without a code change when Google
// retires a version. `gemini-2.0-flash` was retired (returns 404 "no longer
// available"); `gemini-2.5-flash` is the current fast GA model.
const GEMINI_MODEL = process.env.REACT_APP_GEMINI_MODEL || 'gemini-2.5-flash';

// Shared in-memory cache — persists across page navigations within the same session.
const summaryCache = new Map();

const isAiEnabled = () => process.env.REACT_APP_AI_FEATURES_ENABLED === 'true';
const hasApiKey = () => isAiEnabled() && Boolean(process.env.REACT_APP_GEMINI_API_KEY);

/**
 * Shared Gemini call with cache + timeout + graceful fallback.
 * Returns the AI text on success, or null on any failure (so callers can fall back deterministically).
 */
const callGemini = async ({ cacheKey, prompt, maxOutputTokens = 200, temperature = 0.3, timeoutMs = 5000 }) => {
  if (cacheKey && summaryCache.has(cacheKey)) {
    return summaryCache.get(cacheKey);
  }
  // Disabled by default (REACT_APP_AI_FEATURES_ENABLED) or no key present —
  // never call the provider from the browser; fall back to deterministic copy.
  if (!hasApiKey()) return null;

  try {
    const model = genAI.getGenerativeModel({
      model: GEMINI_MODEL,
      generationConfig: { temperature, maxOutputTokens },
    });

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    const result = await model.generateContent(
      { contents: [{ role: 'user', parts: [{ text: prompt }] }] },
      { signal: controller.signal },
    );

    clearTimeout(timeoutId);
    const text = result.response?.text?.()?.trim();
    if (text) {
      if (cacheKey) summaryCache.set(cacheKey, text);
      return text;
    }
  } catch (error) {
    console.warn(`Gemini summary failed (${cacheKey || 'no-cache'}):`, error.message);
  }
  return null;
};

// Cache a deterministic fallback under the same key so we don't recompute it on every render.
const cacheFallback = (cacheKey, text) => {
  if (text && cacheKey) summaryCache.set(cacheKey, text);
  return text;
};

// ============================================================================
// GA4 — "What Changed"
// ============================================================================

const buildGA4Fallback = ({ kpis, channelData }) => {
  const comp = kpis?.comparison;
  if (!comp) return null;
  const sentences = [];

  if (comp.sessions_change_pct != null) {
    const pct = comp.sessions_change_pct;
    let channelNote = '';
    if (channelData?.length) {
      const top = [...channelData].sort((a, b) => (b.sessions || 0) - (a.sessions || 0))[0];
      if (top) {
        const total = channelData.reduce((s, r) => s + (r.sessions || 0), 0);
        const share = total > 0 ? ((top.sessions / total) * 100).toFixed(0) : 0;
        channelNote = `, driven primarily by ${top.channel} (${share}% of traffic)`;
      }
    }
    sentences.push(`Sessions ${pct >= 0 ? 'increased' : 'decreased'} by ${Math.abs(pct).toFixed(1)}%${channelNote}.`);
  }

  if (comp.bounce_rate_change_pct != null && Math.abs(comp.bounce_rate_change_pct) >= 0.5) {
    const pct = comp.bounce_rate_change_pct;
    const meaningful = Math.abs(pct) >= 2;
    const tail = meaningful
      ? (pct < 0 ? ', suggesting better landing page relevance' : ', indicating weaker landing page engagement')
      : '';
    sentences.push(`Bounce rate ${pct > 0 ? 'rose' : 'eased'} by ${Math.abs(pct).toFixed(1)}%${tail}.`);
  }

  if (comp.conversions_change_pct != null) {
    const pct = comp.conversions_change_pct;
    let convNote = '';
    if (channelData?.length) {
      const topConv = [...channelData].sort((a, b) => (b.conversions || 0) - (a.conversions || 0))[0];
      if (topConv && (topConv.conversions || 0) > 0) convNote = `, with ${topConv.channel} contributing the most conversions`;
    }
    sentences.push(`Conversions are ${pct >= 0 ? 'up' : 'down'} ${Math.abs(pct).toFixed(1)}% compared to the previous period${convNote}.`);
  }

  if (comp.total_revenue_change_pct != null && kpis.total_revenue > 0) {
    const pct = comp.total_revenue_change_pct;
    sentences.push(`Revenue ${pct >= 0 ? 'increased' : 'decreased'} by ${Math.abs(pct).toFixed(1)}% to $${Number(kpis.total_revenue).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}.`);
  }

  if (comp.users_change_pct != null && comp.sessions_change_pct != null && Math.abs(comp.users_change_pct - comp.sessions_change_pct) > 5) {
    const pct = comp.users_change_pct;
    sentences.push(`Unique users ${pct >= 0 ? 'grew' : 'declined'} by ${Math.abs(pct).toFixed(1)}%, ${pct > comp.sessions_change_pct ? 'outpacing' : 'lagging'} session growth.`);
  }

  return sentences.length > 0 ? sentences.slice(0, 4).join(' ') : null;
};

export const generateGA4Summary = async ({ clientId, startDate, endDate, kpis, channelData, devicesData }) => {
  const cacheKey = `ga4|${clientId}|${startDate}|${endDate}`;
  if (summaryCache.has(cacheKey)) return { success: true, data: summaryCache.get(cacheKey) };

  const comp = kpis?.comparison;
  if (!comp) return { success: false, error: 'No comparison data' };

  const topChannel = channelData?.length
    ? [...channelData].sort((a, b) => (b.sessions || 0) - (a.sessions || 0))[0]
    : null;
  const totalChannelSessions = channelData?.reduce((s, r) => s + (r.sessions || 0), 0) || 0;
  const totalDeviceSessions = devicesData?.reduce((s, r) => s + (r.sessions || 0), 0) || 0;
  const mobileSessions = devicesData?.find(r => (r.device || '').toLowerCase() === 'mobile')?.sessions || 0;
  const mobilePct = totalDeviceSessions > 0 ? ((mobileSessions / totalDeviceSessions) * 100).toFixed(1) : null;
  const topConvertingChannel = channelData?.length
    ? [...channelData].sort((a, b) => (b.conversions || 0) - (a.conversions || 0))[0]
    : null;

  const prompt = `You are a concise analytics assistant. Given the GA4 data below, write exactly four short sentences in plain English summarizing what changed between the current and previous period. Focus on the most notable changes and tighten every sentence — no filler, no hedging adverbs. Do NOT use bullet points or headings — just flowing sentences.

CURRENT PERIOD KPIs:
- Sessions: ${kpis.sessions ?? 'N/A'}
- Users: ${kpis.users ?? 'N/A'}
- New Users: ${kpis.new_users ?? 'N/A'}
- Bounce Rate: ${kpis.bounce_rate != null ? (kpis.bounce_rate * 100).toFixed(1) + '%' : 'N/A'}
- Conversions: ${kpis.conversions ?? 'N/A'}
- Revenue: ${kpis.total_revenue != null ? '$' + Number(kpis.total_revenue).toFixed(2) : 'N/A'}
- Engagement Rate: ${kpis.engagement_rate != null ? (kpis.engagement_rate * 100).toFixed(1) + '%' : 'N/A'}
- Pageviews: ${kpis.pageviews ?? 'N/A'}
- Avg Session Duration: ${kpis.avg_session_duration != null ? Math.round(kpis.avg_session_duration) + 's' : 'N/A'}
- Top Channel: ${topChannel ? `${topChannel.channel} (${topChannel.sessions} sessions, ${totalChannelSessions > 0 ? ((topChannel.sessions / totalChannelSessions) * 100).toFixed(0) : 0}% of traffic)` : 'N/A'}
- Mobile Traffic: ${mobilePct ? mobilePct + '%' : 'N/A'}

CHANGES VS PREVIOUS PERIOD:
- Sessions: ${comp.sessions_change_pct != null ? comp.sessions_change_pct.toFixed(1) + '%' : 'N/A'}
- Users: ${comp.users_change_pct != null ? comp.users_change_pct.toFixed(1) + '%' : 'N/A'}
- New Users: ${comp.new_users_change_pct != null ? comp.new_users_change_pct.toFixed(1) + '%' : 'N/A'}
- Bounce Rate: ${comp.bounce_rate_change_pct != null ? comp.bounce_rate_change_pct.toFixed(1) + '%' : 'N/A'}
- Conversions: ${comp.conversions_change_pct != null ? comp.conversions_change_pct.toFixed(1) + '%' : 'N/A'}
- Revenue: ${comp.total_revenue_change_pct != null ? comp.total_revenue_change_pct.toFixed(1) + '%' : 'N/A'}
- Engagement Rate: ${comp.engagement_rate_change_pct != null ? comp.engagement_rate_change_pct.toFixed(1) + '%' : 'N/A'}
- Pageviews: ${comp.pageviews_change_pct != null ? comp.pageviews_change_pct.toFixed(1) + '%' : 'N/A'}

ADDITIONAL CONTEXT:
- Top Converting Channel: ${topConvertingChannel && (topConvertingChannel.conversions || 0) > 0 ? `${topConvertingChannel.channel} (${topConvertingChannel.conversions} conversions)` : 'N/A'}

Every "CHANGES VS PREVIOUS PERIOD" value above is a relative percent change versus the previous period — describe them as percentages (e.g. "bounce rate fell 12.6%"), never as "percentage points" or "pp". Write exactly four tight sentences. Be specific with numbers and details. Do not speculate on causes you cannot see in the data. If bounce rate moved less than 2%, just state the number without attributing it to landing page quality, relevance, or any other cause.`;

  const ai = await callGemini({ cacheKey, prompt });
  if (ai) return { success: true, data: ai };

  const fallback = buildGA4Fallback({ kpis, channelData });
  if (fallback) {
    cacheFallback(cacheKey, fallback);
    return { success: true, data: fallback };
  }
  return { success: false, error: 'No summary data available' };
};

// ============================================================================
// AI Monitor — "What Changed" (run-vs-run comparison)
// ============================================================================

// Markup-comment #60: replace raw YYYY-MM-DD dates inside body copy with a
// friendlier "May 18" form. Same-year comparisons drop the year; cross-year
// comparisons keep it ("May 18, 2025").
const humanizeRunDate = (isoDate, refIsoDate) => {
  if (!isoDate) return null;
  const d = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  const month = d.toLocaleString('en-US', { month: 'short' });
  let label = `${month} ${d.getDate()}`;
  if (refIsoDate) {
    const ref = new Date(`${refIsoDate}T00:00:00`);
    if (!Number.isNaN(ref.getTime()) && ref.getFullYear() !== d.getFullYear()) {
      label = `${month} ${d.getDate()}, ${d.getFullYear()}`;
    }
  }
  return label;
};

const buildAimFallback = ({ kpis, prevKpis, runDate, compareRunDate }) => {
  if (!kpis || !prevKpis) return null;
  const sentences = [];
  const pctChange = (cur, prev) => (prev > 0 ? ((cur - prev) / prev) * 100 : null);

  // Lead with the headline metric — the property's mentions.
  const mentions = Number(kpis.client_mentions ?? 0);
  const prevMentions = Number(prevKpis.client_mentions ?? 0);
  const mentionsPct = pctChange(mentions, prevMentions);
  if (mentionsPct != null && Math.abs(mentionsPct) >= 1) {
    const friendlyDate = humanizeRunDate(compareRunDate, runDate);
    const dateNote = friendlyDate ? ` vs the ${friendlyDate} run` : ' vs the previous run';
    sentences.push(`Property mentions ${mentionsPct >= 0 ? 'rose' : 'dropped'} ${Math.abs(mentionsPct).toFixed(1)}% (${prevMentions} → ${mentions})${dateNote}.`);
  } else if (mentions > 0 || prevMentions > 0) {
    sentences.push(`Property mentions held at ${mentions}.`);
  }

  // Hallucinations — accuracy story.
  const hal = Number(kpis.total_hallucinations ?? 0);
  const prevHal = Number(prevKpis.total_hallucinations ?? 0);
  if (hal !== prevHal) {
    const delta = hal - prevHal;
    const tail = delta < 0
      ? ', a sign of cleaner factual coverage'
      : (hal >= prevHal * 1.5 ? ', worth investigating for drifted ground-truth facts' : '');
    sentences.push(`Hallucinations ${delta > 0 ? 'increased' : 'decreased'} by ${Math.abs(delta)} (${prevHal} → ${hal})${tail}.`);
  } else if (hal === 0 && prevHal === 0) {
    sentences.push('No hallucinations detected this run or last.');
  }

  // Citation source breadth — who's amplifying you.
  const cit = Number(kpis.citation_sources ?? 0);
  const prevCit = Number(prevKpis.citation_sources ?? 0);
  if (Math.abs(cit - prevCit) >= 1) {
    sentences.push(`Citation sources ${cit > prevCit ? 'expanded' : 'contracted'} from ${prevCit} to ${cit} domains.`);
  }

  // Competitor field size.
  const comp = Number(kpis.unique_competitors ?? 0);
  const prevComp = Number(prevKpis.unique_competitors ?? 0);
  if (Math.abs(comp - prevComp) >= 1) {
    sentences.push(`AI surfaced ${comp} unique competitors (${prevComp} previously).`);
  }

  return sentences.length > 0 ? sentences.slice(0, 4).join(' ') : null;
};

export const generateAimSummary = async ({ clientId, runId, compareRunId, kpis, prevKpis, runDate, compareRunDate }) => {
  if (!clientId || !runId || !compareRunId || !kpis || !prevKpis) {
    return { success: false, error: 'Missing comparison data' };
  }
  const cacheKey = `aim|${clientId}|${runId}|${compareRunId}`;
  if (summaryCache.has(cacheKey)) return { success: true, data: summaryCache.get(cacheKey) };

  const currentLabel = humanizeRunDate(runDate, compareRunDate) || 'selected';
  const previousLabel = humanizeRunDate(compareRunDate, runDate) || 'previous';
  const prompt = `You are a concise AI-visibility analyst. Compare the two AI Monitor runs below and write exactly four short sentences in plain English describing what changed. Focus on the most notable shifts in the property's mentions, hallucinations, citation sources, and competitor surface area. The dashboard is read by the property owner/operator — refer to their data as "property mentions" or "the property's mentions", never "client mentions". No bullet points, no headings, no hedging — flowing sentences only. Be specific with numbers. When you reference a run, use the friendly date labels exactly as shown (e.g., "the May 18 run") — never use raw YYYY-MM-DD dates in your output.

CURRENT RUN (${currentLabel}):
- Responses analyzed: ${kpis.total_responses ?? 'N/A'}
- Property mentions: ${kpis.client_mentions ?? 'N/A'}
- Unique competitors detected: ${kpis.unique_competitors ?? 'N/A'}
- Citation sources: ${kpis.citation_sources ?? 'N/A'}
- Hallucinations: ${kpis.total_hallucinations ?? 'N/A'}
- Avg latency (ms): ${kpis.avg_latency_ms ?? 'N/A'}

PREVIOUS RUN (${previousLabel}):
- Responses analyzed: ${prevKpis.total_responses ?? 'N/A'}
- Property mentions: ${prevKpis.client_mentions ?? 'N/A'}
- Unique competitors detected: ${prevKpis.unique_competitors ?? 'N/A'}
- Citation sources: ${prevKpis.citation_sources ?? 'N/A'}
- Hallucinations: ${prevKpis.total_hallucinations ?? 'N/A'}
- Avg latency (ms): ${prevKpis.avg_latency_ms ?? 'N/A'}

Write exactly four tight sentences. Do not speculate on causes you cannot see in the data.`;

  const ai = await callGemini({ cacheKey, prompt });
  if (ai) return { success: true, data: ai };

  const fallback = buildAimFallback({ kpis, prevKpis, runDate, compareRunDate });
  if (fallback) {
    cacheFallback(cacheKey, fallback);
    return { success: true, data: fallback };
  }
  return { success: false, error: 'No summary data available' };
};

// ============================================================================
// Google Ads — "What Changed"
// ============================================================================

const fmtPctSigned = (v) => `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`;

const buildGoogleAdsFallback = ({ kpis }) => {
  const comp = kpis?.comparison;
  if (!comp) return null;
  const sentences = [];

  if (comp.spend_change_pct != null && kpis.spend != null) {
    const pct = comp.spend_change_pct;
    sentences.push(`Spend ${pct >= 0 ? 'rose' : 'fell'} ${Math.abs(pct).toFixed(1)}% to $${Number(kpis.spend).toLocaleString(undefined, { maximumFractionDigits: 0 })}.`);
  }
  if (comp.bookings_change_pct != null) {
    const pct = comp.bookings_change_pct;
    sentences.push(`Bookings ${pct >= 0 ? 'increased' : 'decreased'} ${Math.abs(pct).toFixed(1)}%${kpis.tier1_bookings != null ? ` to ${kpis.tier1_bookings}` : ''}.`);
  }
  if (comp.roas_change_pct != null && kpis.roas != null) {
    const pct = comp.roas_change_pct;
    sentences.push(`Return on ad spend ${pct >= 0 ? 'improved' : 'declined'} ${Math.abs(pct).toFixed(1)}% to ${Number(kpis.roas).toFixed(2)}x.`);
  }
  if (comp.cost_per_booking_change_pct != null && kpis.cost_per_booking != null) {
    const pct = comp.cost_per_booking_change_pct;
    sentences.push(`Cost per booking ${pct >= 0 ? 'rose' : 'fell'} ${Math.abs(pct).toFixed(1)}% to $${Number(kpis.cost_per_booking).toFixed(2)}.`);
  }

  return sentences.length > 0 ? sentences.slice(0, 4).join(' ') : null;
};

export const generateGoogleAdsSummary = async ({ clientId, startDate, endDate, kpis, comparisonMode, verdictLabel = null, fallbackText = null }) => {
  const cacheKey = `gads|${clientId}|${startDate}|${endDate}|${comparisonMode}|${verdictLabel || ''}`;
  if (summaryCache.has(cacheKey)) return { success: true, data: summaryCache.get(cacheKey) };

  const comp = kpis?.comparison;
  const compLabel = comparisonMode === 'previous_year' ? 'previous year' : 'previous period';
  const hasComp = !!comp && comparisonMode !== 'none';

  // Review N2 (2026-07-23) — the prompt must state the selected date range.
  // Without it the model narrated a default/rolling window and the banner
  // appeared to ignore the date picker. All KPI values below are already scoped
  // to [startDate, endDate], so anchoring the prompt keeps the narrative and the
  // numbers on the same window.
  const rangeLine = (startDate && endDate)
    ? `These figures cover ONLY the selected date range ${startDate} to ${endDate}. Do not reference any other window (e.g. "last 30 days").`
    : '';

  const prompt = `You are a concise paid-search assistant. Write exactly three short sentences in plain English about this Google Ads account. The first sentence states where the account stands right now (the verdict — efficiency and volume). The second sentence states what moved versus the ${compLabel} (only mention metrics that actually moved). The third sentence states the next-best action. No bullet points or headings — just flowing sentences. Do not repeat the same number twice across sentences.
${rangeLine ? '\n' + rangeLine + '\n' : ''}
CURRENT PERIOD KPIs:
- Total Spend: ${kpis.spend != null ? '$' + Number(kpis.spend).toFixed(2) : 'N/A'}
- Bookings (Tier 1): ${kpis.tier1_bookings ?? 'N/A'}
- Cost per Booking: ${kpis.cost_per_booking != null ? '$' + Number(kpis.cost_per_booking).toFixed(2) : 'N/A'}
- ROAS: ${kpis.roas != null ? Number(kpis.roas).toFixed(2) + 'x' : 'N/A'}
- Phone Calls (Tier 2): ${kpis.tier2_phone_calls ?? 'N/A'}
- All Conversions: ${kpis.all_conversions ?? 'N/A'}
- Clicks: ${kpis.clicks ?? 'N/A'}
- Click-Through Rate: ${kpis.ctr != null ? (kpis.ctr * 100).toFixed(2) + '%' : 'N/A'}
${verdictLabel ? `\nDETERMINISTIC VERDICT LABEL (lead the first sentence with this label): ${verdictLabel}\n` : ''}
${hasComp ? `CHANGES VS ${compLabel.toUpperCase()}:
- Spend: ${comp.spend_change_pct != null ? fmtPctSigned(comp.spend_change_pct) : 'N/A'}
- Bookings: ${comp.bookings_change_pct != null ? fmtPctSigned(comp.bookings_change_pct) : 'N/A'}
- Cost per Booking: ${comp.cost_per_booking_change_pct != null ? fmtPctSigned(comp.cost_per_booking_change_pct) : 'N/A'}
- ROAS: ${comp.roas_change_pct != null ? fmtPctSigned(comp.roas_change_pct) : 'N/A'}
- Phone Calls: ${comp.phone_calls_change_pct != null ? fmtPctSigned(comp.phone_calls_change_pct) : 'N/A'}
- All Conversions: ${comp.all_conversions_change_pct != null ? fmtPctSigned(comp.all_conversions_change_pct) : 'N/A'}
- Clicks: ${comp.clicks_change_pct != null ? fmtPctSigned(comp.clicks_change_pct) : 'N/A'}
- CTR: ${comp.ctr_change_pct != null ? fmtPctSigned(comp.ctr_change_pct) + ' pp' : 'N/A'}` : 'No period-over-period comparison data available — skip the second sentence and write two sentences instead.'}

Be specific with numbers. Do not speculate beyond the data. Do not include the deterministic verdict label literally — incorporate it naturally into the first sentence.`;

  const ai = await callGemini({ cacheKey, prompt, maxOutputTokens: 180 });
  if (ai) return { success: true, data: ai };

  if (fallbackText) {
    cacheFallback(cacheKey, fallbackText);
    return { success: true, data: fallbackText };
  }
  const fallback = buildGoogleAdsFallback({ kpis });
  if (fallback) {
    cacheFallback(cacheKey, fallback);
    return { success: true, data: fallback };
  }
  return { success: false, error: 'No summary data available' };
};

// ============================================================================
// Event Intelligence — "What's Happening"
// ============================================================================

const ecpBand = (score) => {
  if (score == null) return 'unknown';
  if (score >= 70) return 'strong';
  if (score >= 40) return 'moderate';
  if (score >= 20) return 'soft';
  return 'quiet';
};

const buildEventIntelligenceFallback = ({ ecp, ami, velocity, coverage, diversity, urgentCount }) => {
  const sentences = [];
  const ecpScore = Number(ecp?.data?.ecp ?? 0);
  const eventsCount = Number(ecp?.data?.events ?? 0);
  const amiVal = ami?.data?.ami != null ? Number(ami.data.ami) : null;
  const avgLead = velocity?.data?.avg_lead_days != null ? Number(velocity.data.avg_lead_days) : null;
  const covPct = coverage?.data?.coverage_pct != null ? Number(coverage.data.coverage_pct) : null;
  const total = Number(coverage?.data?.total_opportunities ?? 0);
  const ratio = diversity?.data?.diversity_ratio != null ? Number(diversity.data.diversity_ratio) : null;

  const band = ecpBand(ecpScore);
  if (eventsCount > 0) {
    sentences.push(`Event Concentration is ${band} at ${ecpScore.toFixed(1)} across ${eventsCount} scored events.`);
  } else {
    sentences.push(`No scored events in this window — Event Concentration score is ${ecpScore.toFixed(1)}.`);
  }

  if (urgentCount > 0) {
    sentences.push(`${urgentCount} urgent opportunit${urgentCount === 1 ? 'y is' : 'ies are'} within 14 days and need playbook attention.`);
  } else if (amiVal != null) {
    sentences.push(`Audience Match Index sits at ${amiVal.toFixed(0)}%${amiVal >= 60 ? ' — fit is strong' : amiVal >= 30 ? ' — fit is mixed' : ' — fit is weak'}.`);
  }

  if (covPct != null && total > 0) {
    sentences.push(`Playbook coverage is at ${covPct.toFixed(0)}% across ${total} opportunit${total === 1 ? 'y' : 'ies'}.`);
  }

  if (avgLead != null && avgLead > 0) {
    sentences.push(`Average lead time before events is ${avgLead.toFixed(0)} days${ratio != null ? `, with source mix at ${(ratio * 100).toFixed(0)}% diversity` : ''}.`);
  } else if (ratio != null) {
    sentences.push(`Source diversity is ${(ratio * 100).toFixed(0)}% across signal providers.`);
  }

  return sentences.length > 0 ? sentences.slice(0, 4).join(' ') : null;
};

export const generateEventIntelligenceSummary = async ({ clientId, startDate, endDate, ecp, ami, velocity, coverage, diversity, urgentCount = 0, verdictLabel = null, fallbackText = null }) => {
  const cacheKey = `ei|${clientId}|${startDate}|${endDate}|${verdictLabel || ''}`;
  if (summaryCache.has(cacheKey)) return { success: true, data: summaryCache.get(cacheKey) };

  const ecpScore = Number(ecp?.data?.ecp ?? 0);
  const eventsCount = Number(ecp?.data?.events ?? 0);
  const amiVal = ami?.data?.ami != null ? Number(ami.data.ami) : null;
  const avgLead = velocity?.data?.avg_lead_days != null ? Number(velocity.data.avg_lead_days) : null;
  const minLead = velocity?.data?.min_lead != null ? Number(velocity.data.min_lead) : null;
  const maxLead = velocity?.data?.max_lead != null ? Number(velocity.data.max_lead) : null;
  const covPct = coverage?.data?.coverage_pct != null ? Number(coverage.data.coverage_pct) : null;
  const withPb = Number(coverage?.data?.with_playbook ?? 0);
  const total = Number(coverage?.data?.total_opportunities ?? 0);
  const ratio = diversity?.data?.diversity_ratio != null ? Number(diversity.data.diversity_ratio) : null;
  const tm = Number(diversity?.data?.tm_count ?? 0);
  const serp = Number(diversity?.data?.serp_count ?? 0);

  // If we have essentially nothing, skip the AI call.
  if (eventsCount === 0 && total === 0) return { success: false, error: 'No event data available' };

  const prompt = `You are a concise event-marketing assistant. Write exactly three short sentences in plain English about this event-marketing pipeline for ${startDate} to ${endDate}. The first sentence states the pipeline health (the verdict — concentration and coverage). The second sentence calls out the highest-leverage signal (urgent events if any, otherwise audience fit or lead time). The third sentence states the next-best action. No bullet points or headings — just flowing sentences. Do not repeat the same number twice across sentences.

EVENT INTELLIGENCE SNAPSHOT:
- Event Concentration (ECP): ${ecpScore.toFixed(1)} across ${eventsCount} scored events
- Audience Match Index (AMI): ${amiVal != null ? amiVal.toFixed(1) + '%' : 'N/A'}
- Marketing Velocity (avg lead days): ${avgLead != null ? avgLead.toFixed(1) : 'N/A'}${minLead != null && maxLead != null ? ` (range ${minLead.toFixed(0)}–${maxLead.toFixed(0)} days)` : ''}
- Playbook Coverage: ${covPct != null ? covPct.toFixed(1) + '%' : 'N/A'} (${withPb} of ${total} opportunities have playbooks)
- Source Diversity: ${ratio != null ? (ratio * 100).toFixed(0) + '%' : 'N/A'} (${tm} Ticketmaster, ${serp} SERP)
- Urgent Opportunities (<14 days lead, score ≥ 50): ${urgentCount}
${verdictLabel ? `\nDETERMINISTIC VERDICT LABEL (lead the first sentence with this label, naturally — don't quote it literally): ${verdictLabel}\n` : ''}
Be specific with numbers. Do not speculate beyond the data.`;

  const ai = await callGemini({ cacheKey, prompt, maxOutputTokens: 180 });
  if (ai) return { success: true, data: ai };

  if (fallbackText) {
    cacheFallback(cacheKey, fallbackText);
    return { success: true, data: fallbackText };
  }
  const fallback = buildEventIntelligenceFallback({ ecp, ami, velocity, coverage, diversity, urgentCount });
  if (fallback) {
    cacheFallback(cacheKey, fallback);
    return { success: true, data: fallback };
  }
  return { success: false, error: 'No summary data available' };
};

// ============================================================================
// Competitive Intelligence — AI-first verdict narrative
// ============================================================================

export const generateCISummary = async ({ clientId, summary, actionsCount = 0, fallbackText = null }) => {
  // CI is forward-looking — no date range. Cache key uses the headline metrics so the
  // AI re-runs only when the inputs that matter change.
  const m = summary?.data?.metrics;
  const compSet = summary?.data?.comp_set;
  const rateChanges = summary?.data?.rate_changes;
  const rpiKey = m?.rpi != null ? Number(m.rpi).toFixed(1) : 'na';
  const opiKey = m?.opi != null ? Number(m.opi).toFixed(1) : 'na';
  const cacheKey = `ci|${clientId}|${rpiKey}|${opiKey}|${actionsCount}`;
  if (summaryCache.has(cacheKey)) return { success: true, data: summaryCache.get(cacheKey) };

  if (!m || m.rpi == null || m.opi == null) {
    return { success: false, error: 'Insufficient metrics' };
  }

  const prompt = `You are a concise revenue-management assistant. Write exactly three short sentences in plain English summarizing this property's competitive position for the next 90 days. Lead with the verdict (winning / watch / act-now / reposition / balanced) based on RPI and OPI. No bullet points or headings — just flowing sentences.

COMPETITIVE INTELLIGENCE SNAPSHOT:
- Rate Position Index (RPI, 100 = at market): ${Number(m.rpi).toFixed(1)}
- Occupancy Position Index (OPI, 100 = at market): ${Number(m.opi).toFixed(1)}
- Client Avg Rate: ${m.client_avg_rate != null ? '$' + Number(m.client_avg_rate).toFixed(2) : 'N/A'}
- Comp Set Avg Rate: ${m.comp_set_avg_rate != null ? '$' + Number(m.comp_set_avg_rate).toFixed(2) : 'N/A'}
- Client Inferred Occupancy: ${m.client_inferred_occupancy_avg != null ? '~' + (Number(m.client_inferred_occupancy_avg) * 100).toFixed(1) + '%' : 'N/A'}
- Comp Set Inferred Occupancy: ${m.comp_set_inferred_occupancy_avg != null ? '~' + (Number(m.comp_set_inferred_occupancy_avg) * 100).toFixed(1) + '%' : 'N/A'}
- Comp Set: ${compSet ? `${compSet.direct_count ?? 0} direct, ${compSet.hotels_count ?? 0} hotels, ${compSet.airbnb_count ?? 0} airbnb` : 'N/A'}
- Rate Changes This Period: ${rateChanges ? `${rateChanges.total ?? 0} total (${rateChanges.increases ?? 0} up, ${rateChanges.decreases ?? 0} down)` : 'N/A'}
- AI Pricing Actions Suggested: ${actionsCount}

Quadrant rules: RPI > 110 = premium, 90–110 = at market, < 90 = below market. OPI > 110 = filling faster, 90–110 = in line, < 90 = filling slower. High RPI + High OPI = Winning. High RPI + Low OPI = Watch. Low RPI + High OPI = Act now. Low RPI + Low OPI = Reposition.

Write exactly three tight sentences. First sentence states the verdict label and the headline numbers. Second sentence gives the implication for pricing. Third sentence is the next-best action${actionsCount > 0 ? ' and references the AI Pricing Actions count' : ''}.`;

  const ai = await callGemini({ cacheKey, prompt, maxOutputTokens: 180 });
  if (ai) return { success: true, data: ai };

  // Fallback: caller-provided deterministic copy (from computeVerdict in VerdictCard).
  if (fallbackText) {
    cacheFallback(cacheKey, fallbackText);
    return { success: true, data: fallbackText };
  }
  return { success: false, error: 'No summary data available' };
};
